import TagManager from '@sooro-io/react-gtm-module';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/store';
import { buildDataLayerPayinEvent } from './helpers';
import { GTM_EVENTS } from './gtmSlice';
import { GtmEventChangeInterface } from '@/services/gtm/gtmSlice';

function HandleGtmPayinEvents(currentState: RootState, action: PayloadAction<GtmEventChangeInterface>) {
  const { eventType: actionName, cartId } = action.payload;

  const currentJourney = currentState.journeys.journey;
  const { previousUserChoice } = currentState.gtm;
  const transactionState = currentState.transaction;
  const { audience: thisAudience, activities } = currentState.payin;
  const referringUrl = currentState.urlOverrides.siteUrlOverride;

  const theseDataLayerEventNames: string[] = [];
  let removeFromCart = false;
  let removeEvent;

  // Grab various bits of state we need:
  const {
    amount: currentAmount,
    provider: thisProvider,
    currency
  } = currentState.paymentSelect;

  switch (actionName) {
    case GTM_EVENTS.FRONT_PAGE_LOAD:
    case GTM_EVENTS.FRONT_PAGE_CHANGE:
      theseDataLayerEventNames.push('view_item_list');
      break;

    case GTM_EVENTS.PAYIN_ACTIVITY_PAGE:
      // Multiple DataLayer events for this scenario:
      theseDataLayerEventNames.push('view_item', 'select_item', 'begin_checkout', 'add_to_cart');

      if (previousUserChoice) {
        const { previousAmount, previousJourney } = previousUserChoice;
        // If there HAS been a change to either the amount or journey type, update
        // the flag accordingly to create the 'remove_from_cart' DL event later:
        removeFromCart = previousAmount !== currentAmount || previousJourney !== currentJourney;
      }
      break;

    case GTM_EVENTS.AFTER_PAYMENT_PAGE:
      theseDataLayerEventNames.push('add_payment_info', 'add_shipping_info', 'purchase');
      break;

      /**
       * And just in case, stop things going any further if it's a different event we don't care about:
      */
    default:
      console.error('*** Unknown GTM event:', actionName);
      return;
  }

  const thisAmount = currentAmount || undefined;
  const thisTransactionId = transactionState.transactionId || undefined;

  // State conditions required before running the main code:
  const validConfig = currentJourney && cartId;

  if (validConfig) {
    // Creates a separate remove_from_cart event, using an object based on the cached 'previous' user choice details:
    if (removeFromCart && previousUserChoice) {
      // Grab our previously-selected userChoice config:
      const { previousAmount } = previousUserChoice;

      // Build our removal event with it:
      removeEvent = buildDataLayerPayinEvent({
        eventName: 'remove_from_cart',
        currency,
        cartId,
        value: previousAmount,
        audience: thisAudience,
        referringUrl,
      });
    }

    const everyDataLayerEvent = theseDataLayerEventNames.map(eventName => buildDataLayerPayinEvent({
      eventName,
      currency,
      cartId,
      value: thisAmount,
      audience: thisAudience,
      paymentType: thisProvider,
      transactionId: thisTransactionId,
      activities,
      referringUrl
    }));

    // Iterate over this array to fire the DataLayer events:
    everyDataLayerEvent.forEach((thisDataLayerEvent) => {
      // Required before EVERY DL event as a means of reset:
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
          site: [{
            category: 'cr-payin',
            pageCategory: 'cr_payin',
            cartID: cartId,
          }],
        }
      });

      // Fire this event
      TagManager.dataLayer({
        dataLayer: thisDataLayerEvent
      });
    });

    // And FINALLY, IF we produced a remove_from_cart event, fire it off here:
    if (removeEvent) {
      // Again, required before every DL event
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
          site: [{
            category: 'cr-payin',
            pageCategory: 'cr_payin',
            cartID: cartId,
          }],
        }
      });

      TagManager.dataLayer({
        dataLayer: removeEvent
      });
    }
  }
}

export { HandleGtmPayinEvents };
